<template>
  <BaseWidget
    :deviceIds="deviceIds"
    :widgetId="widgetId"
    :widgetType="widgetType"
    :widgetMaxDevices="widgetMaxDevices"
    :title="name"
    :subtitle="deviceType"
    :fullscreen="fullscreen"
    @removeDevice="removeDevice"
    @removeWidget="removeWidget"
    @refreshDashboard="refreshDashboard"
    data-cy="MapWidget"
  >
    <template v-slot:content>
      <div class="map-wrapper">
        <div class="update-bar">
          <v-row no-gutters>
            <v-col cols="12" sm="6"></v-col>
            <v-col cols="12" sm="6">
              <div class="update-bar__item update-bar__item--last-contact">
                {{ $t('map.deviceInfo.lastContact') }}:
                {{ getTimeSinceDate(lastContact) }}
              </div>
            </v-col>
          </v-row>
        </div>
        <GmvMap
          ref="gmap"
          class="google-map"
          :map-key="widgetId"
          :center="center"
          :zoom="zoom"
          map-type-id="terrain"
          :options="options"
          @zoom_changed="updateZoom"
          @center_changed="updateCenter"
        >
          <MapPin
            :marker-key="markerKey"
            :key="deviceId"
            :ref="deviceId"
            :deviceId="deviceId"
            :widget="true"
          />
        </GmvMap>
        <MapLegend class="map-legend" isWidget />
      </div>
    </template>
  </BaseWidget>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import debounce from 'lodash/debounce'
import BaseWidget from './BaseWidget.vue'
import MapPin from '@/components/MapPin.vue'
import MapLegend from '@/components/MapLegend.vue'
import { deviceModelDisplay, getRelativeTimeSince } from '@/services/map'
import { saveWidgetSettings } from '@/services/dashboard'
import { defaultMapLocation } from '@/store/map/utils'
import { NAMESPACES } from '@/store'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { GmvMapRef } from '@/types/map'
import type { WidgetSettings } from '@/types/dashboard'
import type { WidgetComponentKey } from '@/views/dashboard.vue'

export default defineComponent({
  name: 'MapWidget',
  components: {
    BaseWidget,
    MapPin,
    MapLegend
  },
  props: {
    widgetId: String,
    widgetType: String as PropType<WidgetComponentKey>,
    widgetMaxDevices: Object as PropType<Record<WidgetComponentKey, number>>,
    deviceIds: {
      type: Array as PropType<string[]>,
      default: (): string[] => []
    },
    width: Number,
    height: Number,
    settings: {
      type: Object as PropType<WidgetSettings>,
      default: undefined
    }
  },
  emits: ['removeWidget', 'refreshDashboard'],
  data() {
    return {
      fullscreen: false as boolean,
      center: defaultMapLocation.center,
      zoom: defaultMapLocation.zoom as number,
      options: {
        mapId: APP_ENV.VITE_APP_GMAP_MAP_ID,
        clickableIcons: false,
        streetViewControl: false,
        zoomControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        minZoom: 4,
        maxZoom: 24,
        restriction: {
          latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180
          }
        }
      }
    }
  },
  computed: {
    deviceId(): string | undefined {
      return this.deviceIds?.[0]
    },
    name(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    deviceModel(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    deviceType(): string {
      const modelKey = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
      ](this.deviceModel)
      return deviceModelDisplay(modelKey, this.$t.bind(this))
    },
    lastContact(): Date {
      return new Date(
        this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_CONTACT_BY_DEVICE_ID}`
        ](this.deviceId)
      )
    },
    coords(): { lat: number; lng: number } | null {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    markerKey(): string {
      return `${this.widgetId}-${this.deviceId}`
    }
  },
  async mounted() {
    await this.setControls()
    this.applyWidgetSettings()
    if (JSON.stringify(this.center) === JSON.stringify(defaultMapLocation.center)) {
      this.setCenter()
    }
    this.updateCenter = debounce(this.updateCenter, 250)
  },
  watch: {
    deviceId() {
      this.setCenter()
    }
  },
  methods: {
    async setControls() {
      await this.$gmapApiPromiseLazy()
      const map = await (this.$refs.gmap as GmvMapRef)?.mapPromise
      map?.setOptions(this.options)
    },
    setCenter(): void {
      const center = this.coords
      if (center) {
        this.center = center
      } else {
        const unwatch = this.$store.watch(
          (state) => state.devices.coords,
          () => {
            this.setCenter()
            unwatch()
          }
        )
      }
    },
    updateZoom(zoom: number): void {
      if (zoom) {
        saveWidgetSettings(this.widgetId as string, { zoom })
      }
    },
    updateCenter(coords: { lat: () => number; lng: () => number }): void {
      if (coords) {
        const center = {
          lat: coords.lat(),
          lng: coords.lng()
        }
        saveWidgetSettings(this.widgetId as string, { center })
      }
    },
    applyWidgetSettings(): void {
      if (this.settings?.center?.lat && this.settings?.center?.lng) {
        this.center = this.settings.center
      }
      if (this.settings?.zoom) {
        this.zoom = this.settings.zoom
      }
    },
    getTimeSinceDate(date: Date): string {
      return getRelativeTimeSince(date, this.$t.bind(this))
    },
    removeDevice(_index: number, options?: unknown): void {
      this.removeWidget(options)
    },
    removeWidget(options?: unknown): void {
      this.$emit('removeWidget', options)
    },
    refreshDashboard(): void {
      this.$emit('refreshDashboard')
    }
  }
})
</script>

<style lang="scss" scoped>
.v-card {
  overflow: hidden;
}
.map-wrapper {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin: -12px;
}
.update-bar {
  position: relative;
  z-index: 1;
  padding: 0 12px;
  color: rgba(0, 0, 0, 0.86);
  background-color: #fff;

  &__item {
    font-size: 0.75rem;
    line-height: 1.5;

    &--last-contact {
      @media (min-width: 600px) {
        text-align: right;
      }
    }
  }
}
.google-map {
  flex-grow: 1;
}

.map-legend {
  position: absolute;
  bottom: 0;
  left: 0;
}
</style>
